// Site QR / self-service sign-on manager + printable poster.
// Lets internal staff / site managers generate a persistent QR a worker can scan to
// self-complete a site induction (or other form) on their own phone.
import { useEffect, useState, useCallback } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { QrCode, Printer, RefreshCw, Copy, Power, ClipboardCheck, X, Users, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

const BRAND = "var(--brand-accent, #C75A1B)";

function publicUrl(token) {
    return `${window.location.origin}/f/${token}`;
}

export default function SiteQRPoster({ clientId, siteId, projectId, siteName, projectName, templateCode = "SITE-INDUCTION", templateLabel = "Site Induction", templateId = null, mode = "individual", templateCategory = "induction", pickTemplate = false, compact = false }) {
    const [links, setLinks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [posterToken, setPosterToken] = useState(null);
    const isAttendance = mode === "attendance";
    const isRegister = mode === "register";
    const isToolbox = templateCategory === "toolbox";
    // optional template picker (self-contained) — manager chooses which induction/talk the QR issues
    const [pickTpls, setPickTpls] = useState([]);
    const [pickedId, setPickedId] = useState(null);
    useEffect(() => {
        if (!pickTemplate || isAttendance || !clientId) return;
        api.get(isToolbox ? `/toolbox/templates?client_id=${clientId}` : `/inductions/templates?client_id=${clientId}`).then((r) => {
            const active = (r.data || []).filter((t) => t.status === "published");
            setPickTpls(active);
            const def = active.find((t) => t.is_default) || active[0];
            if (def) setPickedId(def.id);
        }).catch(() => {});
    }, [pickTemplate, isAttendance, clientId, isToolbox]);
    const effTemplateId = pickTemplate ? pickedId : templateId;
    const pickedTpl = pickTpls.find((t) => t.id === pickedId);

    const load = useCallback(async () => {
        if (!clientId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (siteId) params.set("site_id", siteId);
            else params.set("client_id", clientId);
            const r = await api.get(`/site-forms?${params.toString()}`);
            setLinks((r.data || []).filter((l) => {
                if (isAttendance) return l.mode === "attendance";
                if (effTemplateId) return l.template_id === effTemplateId;
                return l.template_code === templateCode && l.mode !== "attendance";
            }));
        } catch { /* noop */ }
        setLoading(false);
    }, [clientId, siteId, templateCode, effTemplateId, isAttendance]);

    useEffect(() => { load(); }, [load]);

    async function createLink() {
        if (pickTemplate && !isAttendance && !effTemplateId) {
            toast.error(isToolbox ? "Pick a talk first" : "Pick an induction first");
            return;
        }
        try {
            const label = pickTemplate && pickedTpl ? pickedTpl.name : templateLabel;
            const r = await api.post("/site-forms", {
                client_id: clientId, site_id: siteId, project_id: projectId,
                template_code: isAttendance ? undefined : (pickTemplate ? pickedTpl?.code : templateCode),
                template_id: isAttendance ? undefined : (effTemplateId || undefined),
                mode: isAttendance ? "attendance" : (isRegister ? "register" : "individual"),
                label: isAttendance
                    ? `${siteName || projectName || "Site"} — Sign-in / out`
                    : `${siteName || projectName || "Site"} — ${label}`,
                section_toggles: isAttendance ? {} : { knowledge_test: true },
            });
            toast.success(isAttendance ? "Sign-in / out link created" : isRegister ? `${label} register QR created` : `${label} sign-on link created`);
            setLinks((l) => [r.data, ...l]);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not create link");
        }
    }

    const [registerView, setRegisterView] = useState(null); // link | null — signed-rows preview
    const [closing, setClosing] = useState(null);           // link.id being closed

    async function closeRegister(link) {
        if (!window.confirm(`Close out "${link.label}"? This files the signed register to the Document Library and deactivates this QR.`)) return;
        setClosing(link.id);
        try {
            const r = await api.post(`/site-forms/${link.id}/close-register`);
            setLinks((l) => l.map((x) => (x.id === link.id ? r.data : x)));
            toast.success("Register closed — filed to the Document Library");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not close the register");
        }
        setClosing(null);
    }

    async function viewRegister(link) {
        try {
            const r = await api.get(`/site-forms/${link.id}/register`);
            setRegisterView({ link, ...r.data });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not load the register");
        }
    }

    async function toggleTest(link) {
        const on = link.section_toggles?.knowledge_test !== false;
        try {
            const r = await api.patch(`/site-forms/${link.id}`, { section_toggles: { ...link.section_toggles, knowledge_test: !on } });
            setLinks((l) => l.map((x) => (x.id === link.id ? r.data : x)));
        } catch { toast.error("Update failed"); }
    }

    async function toggleActive(link) {
        try {
            const r = await api.patch(`/site-forms/${link.id}`, { active: !link.active });
            setLinks((l) => l.map((x) => (x.id === link.id ? r.data : x)));
        } catch { toast.error("Update failed"); }
    }

    async function rotate(link) {
        if (!window.confirm("Generate a new link? The current QR / URL will stop working.")) return;
        try {
            const r = await api.post(`/site-forms/${link.id}/rotate`);
            setLinks((l) => l.map((x) => (x.id === link.id ? r.data : x)));
            toast.success("New link generated — reprint the poster");
        } catch { toast.error("Rotate failed"); }
    }

    function copyUrl(token) {
        navigator.clipboard?.writeText(publicUrl(token)).then(() => toast.success("Link copied")).catch(() => {});
    }

    return (
        <div data-testid="site-qr-poster" className="bg-white border rounded-[13px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
            <div className="px-5 py-3.5 flex items-center gap-2 border-b" style={{ borderColor: "#F0EDE8" }}>
                <QrCode className="w-4 h-4" style={{ color: BRAND }} />
                <h3 className="text-[13.5px] font-semibold text-[#1F1F1F] flex-1">
                    {isAttendance ? "Site sign-in / out — QR" : isRegister ? "Toolbox Talk — QR register" : (templateCode === "SITE-INDUCTION" ? "Site sign-on / QR" : `${templateLabel} — QR sign-on`)}
                </h3>
                <button data-testid="qr-create-link" onClick={createLink}
                    className="text-[12px] font-semibold rounded-full px-3 py-1.5 text-white" style={{ background: BRAND }}>
                    {isRegister ? "+ New register QR" : "+ New sign-on link"}
                </button>
            </div>

            <div className="p-4">
                <p className="text-[12px] text-[#6F6A64] mb-3">
                    {isAttendance
                        ? "Print a QR poster for the site entrance. Visitors and contractors scan it to sign in on arrival and sign out when they leave — no app or login needed. The site manager sees who's on site now."
                        : isRegister
                        ? "Print a QR poster for wherever the talk is delivered. Everyone attending scans it on their own phone and signs the shared register — no app or login, no per-person form. Once the talk's done, close out the register to file the signed sheet in your Document Library."
                        : `Print a QR poster for the site hoarding. Workers scan it on their own phone to self-complete the ${templateLabel.toLowerCase()} — no app or login needed. Each submission lands in your Inspections list for review.`}
                </p>
                {pickTemplate && !isAttendance && (
                    <div data-testid="qr-template-picker" className="mb-3 rounded-lg border p-3" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                        <label className="text-[12px] font-semibold text-[#1F1F1F]">{isToolbox ? "Talk to issue" : "Induction to issue"}</label>
                        <select data-testid="qr-template-select" value={pickedId || ""} onChange={(e) => setPickedId(e.target.value)}
                            className="w-full h-9 px-2.5 rounded-lg border text-[13px] mt-1.5 bg-white" style={{ borderColor: "#E5E0DA" }}>
                            {pickTpls.length === 0 && <option value="">{isToolbox ? "No talks yet" : "No induction templates"}</option>}
                            {pickTpls.map((t) => <option key={t.id} value={t.id}>{t.name}{t.is_default ? " (default)" : ""}</option>)}
                        </select>
                        {pickedTpl?.description && <p className="text-[11.5px] text-[#6F6A64] mt-1.5">{pickedTpl.description}</p>}
                        <p className="text-[11px] text-[#9A968F] mt-1.5">{isRegister ? "The QR is locked to this talk — everyone who scans it signs the same shared register." : "The QR is locked to this induction — workers scan and complete it directly."}</p>
                    </div>
                )}
                {loading ? (
                    <div className="text-[12px] text-[#9A968F] py-4 text-center">Loading…</div>
                ) : links.length === 0 ? (
                    <div className="text-[12px] text-[#9A968F] py-6 text-center border border-dashed rounded-lg" style={{ borderColor: "#E5E0DA" }}>
                        {isRegister ? "No registers yet. Create one to generate a QR poster." : "No sign-on links yet. Create one to generate a QR poster."}
                    </div>
                ) : (
                    <ul className="space-y-2.5">
                        {links.map((link) => {
                            const testOn = link.section_toggles?.knowledge_test !== false;
                            const closed = !!link.register_closed_at;
                            return (
                                <li key={link.id} data-testid={`qr-link-${link.id}`}
                                    className="border rounded-lg p-3" style={{ borderColor: "#E5E0DA", opacity: link.active ? 1 : 0.6 }}>
                                    <div className="flex items-start gap-3">
                                        <div className="bg-white p-1.5 rounded border" style={{ borderColor: "#E5E0DA" }}>
                                            <QRCodeCanvas value={publicUrl(link.token)} size={64} data-testid={`qr-canvas-${link.id}`} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-[13px] font-semibold text-[#1F1F1F] truncate">{link.label}</div>
                                            <div className="text-[11px] text-[#9A968F] mt-0.5 flex items-center gap-2">
                                                <span className="inline-flex items-center gap-1"><Users size={11} /> {link.submission_count || 0} {isRegister ? "signed" : "completed"}</span>
                                                {closed && <span className="font-semibold" style={{ color: "#2E7D4F" }}>Closed — filed to Document Library</span>}
                                                {!closed && !link.active && <span className="text-red-500 font-semibold">Inactive</span>}
                                            </div>
                                            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                                {!closed && <button data-testid={`qr-print-${link.id}`} onClick={() => setPosterToken(link)}
                                                    className="text-[11px] inline-flex items-center gap-1 rounded-full px-2.5 py-1 border font-medium" style={{ borderColor: "#E5E0DA", color: BRAND }}>
                                                    <Printer size={12} /> Print poster
                                                </button>}
                                                {!closed && <button data-testid={`qr-copy-${link.id}`} onClick={() => copyUrl(link.token)}
                                                    className="text-[11px] inline-flex items-center gap-1 rounded-full px-2.5 py-1 border font-medium" style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>
                                                    <Copy size={12} /> Copy link
                                                </button>}
                                                {!isAttendance && !isRegister && <button data-testid={`qr-toggle-test-${link.id}`} onClick={() => toggleTest(link)}
                                                    className="text-[11px] inline-flex items-center gap-1 rounded-full px-2.5 py-1 border font-medium"
                                                    style={testOn ? { borderColor: BRAND, color: BRAND, background: "#FBEFE5" } : { borderColor: "#E5E0DA", color: "#9A968F" }}>
                                                    <ClipboardCheck size={12} /> Knowledge test: {testOn ? "On" : "Off"}
                                                </button>}
                                                {isRegister ? (
                                                    <>
                                                        <button data-testid={`qr-view-register-${link.id}`} onClick={() => viewRegister(link)}
                                                            className="text-[11px] inline-flex items-center gap-1 rounded-full px-2.5 py-1 border font-medium" style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>
                                                            <ClipboardCheck size={12} /> View register
                                                        </button>
                                                        {!closed && <button data-testid={`qr-close-register-${link.id}`} disabled={closing === link.id || !link.submission_count}
                                                            onClick={() => closeRegister(link)}
                                                            className="text-[11px] inline-flex items-center gap-1 rounded-full px-2.5 py-1 border font-semibold disabled:opacity-50"
                                                            style={{ borderColor: BRAND, color: BRAND, background: "#FBEFE5" }}>
                                                            <CheckCircle2 size={12} /> {closing === link.id ? "Closing…" : "Close register"}
                                                        </button>}
                                                    </>
                                                ) : (
                                                    <button data-testid={`qr-toggle-active-${link.id}`} onClick={() => toggleActive(link)}
                                                        className="text-[11px] inline-flex items-center gap-1 rounded-full px-2.5 py-1 border font-medium" style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>
                                                        <Power size={12} /> {link.active ? "Deactivate" : "Activate"}
                                                    </button>
                                                )}
                                                {!closed && <button data-testid={`qr-rotate-${link.id}`} onClick={() => rotate(link)}
                                                    className="text-[11px] inline-flex items-center gap-1 rounded-full px-2.5 py-1 border font-medium" style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>
                                                    <RefreshCw size={12} /> New link
                                                </button>}
                                            </div>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {posterToken && <PosterModal link={posterToken} siteName={siteName} projectName={projectName} isAttendance={isAttendance} isRegister={isRegister} onClose={() => setPosterToken(null)} />}
            {registerView && <RegisterViewModal view={registerView} onClose={() => setRegisterView(null)} />}
        </div>
    );
}

function RegisterViewModal({ view, onClose }) {
    const { link, rows, closed, talk_name } = view;
    return (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="register-view-modal" onClick={onClose}>
            <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: "#F0EDE8" }}>
                    <div>
                        <div className="text-[13px] font-bold text-[#1F1F1F]">{link.label}</div>
                        {talk_name && <div className="text-[11px] text-[#9A968F]">{talk_name}</div>}
                    </div>
                    <button data-testid="register-view-close" onClick={onClose} className="text-[#9A968F] hover:text-[#1F1F1F]"><X className="w-4.5 h-4.5" /></button>
                </div>
                <div className="p-4 max-h-[60vh] overflow-y-auto">
                    {closed && <div className="mb-3 text-[11.5px] font-semibold rounded-lg px-3 py-2" style={{ background: "#EAF6EE", color: "#2E7D4F" }}>Closed — filed to the Document Library.</div>}
                    {rows.length === 0 ? (
                        <p className="text-[12px] text-[#9A968F] text-center py-6">No sign-ins yet.</p>
                    ) : (
                        <ul className="space-y-2" data-testid="register-view-rows">
                            {rows.map((r, i) => (
                                <li key={i} data-testid={`register-row-${i}`} className="flex items-center justify-between border-b last:border-b-0 pb-2" style={{ borderColor: "#F0EDE8" }}>
                                    <div>
                                        <div className="text-[13px] font-semibold text-[#1F1F1F]">{r.name}</div>
                                        <div className="text-[11px] text-[#9A968F]">{r.company || "—"}</div>
                                    </div>
                                    <span className="text-[11px] text-[#9A968F]">{r.date}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

function PosterModal({ link, siteName, projectName, isAttendance, isRegister, onClose }) {
    const url = publicUrl(link.token);
    return (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4 print:bg-white print:p-0" data-testid="qr-poster-modal">
            <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden print:rounded-none print:max-w-none print:shadow-none">
                <div className="flex items-center justify-between px-5 py-3 border-b print:hidden" style={{ borderColor: "#F0EDE8" }}>
                    <span className="text-[13px] font-bold text-[#1F1F1F]">Printable site poster</span>
                    <div className="flex items-center gap-2">
                        <button data-testid="qr-poster-print" onClick={() => window.print()} className="text-[12px] font-semibold rounded-full px-3 py-1.5 text-white inline-flex items-center gap-1.5" style={{ background: BRAND }}>
                            <Printer size={13} /> Print
                        </button>
                        <button onClick={onClose} className="text-[#9A968F] hover:text-[#1F1F1F]"><X size={18} /></button>
                    </div>
                </div>
                <div className="p-10 text-center print:p-16" data-testid="qr-poster-content">
                    <div className="text-[13px] font-bold uppercase tracking-[0.15em]" style={{ color: BRAND }}>{isAttendance ? "Site Sign-In / Sign-Out" : isRegister ? "Toolbox Talk — Sign the Register" : "Site Induction — Sign On"}</div>
                    <h1 className="text-3xl font-extrabold text-[#1F1F1F] mt-3">{siteName || projectName || link.label}</h1>
                    <p className="text-[15px] text-[#6F6A64] mt-2">{isAttendance
                        ? "Scan the QR code with your phone camera to sign in on arrival — and again to sign out when you leave site."
                        : isRegister
                        ? "Scan the QR code with your phone camera, read the briefing and sign the shared register to confirm you attended this talk."
                        : "Scan the QR code with your phone camera to complete your site induction before starting work."}</p>
                    <div className="my-8 inline-block p-5 rounded-2xl border-2" style={{ borderColor: BRAND }}>
                        <QRCodeCanvas value={url} size={260} level="M" />
                    </div>
                    <ol className="text-left max-w-xs mx-auto text-[14px] text-[#1F1F1F] space-y-2 mb-6">
                        <li><b>1.</b> Open your phone camera</li>
                        <li><b>2.</b> Point it at the QR code</li>
                        {isAttendance ? (<>
                            <li><b>3.</b> Tap the link and choose Sign in or Sign out</li>
                            <li><b>4.</b> Remember to sign out when you leave</li>
                        </>) : isRegister ? (<>
                            <li><b>3.</b> Read the briefing</li>
                            <li><b>4.</b> Add your name and sign to confirm attendance</li>
                        </>) : (<>
                            <li><b>3.</b> Tap the link and complete the induction</li>
                            <li><b>4.</b> See the site manager to be authorised on site</li>
                        </>)}
                    </ol>
                    <div className="text-[11px] text-[#9A968F] break-all">{url}</div>
                    <div className="text-[11px] text-[#9A968F] mt-6 font-semibold uppercase tracking-wider">No app or login required · Secured by Priority Risk</div>
                </div>
            </div>
        </div>
    );
}
